import React from 'react';
/**
 * Feedback Section Builder — Constructs accordion sections for the detail phase.
 *
 * Extracted from FeedbackDisplay.tsx (691→~300 lines) during CL-1 prep s62.
 * Co-located in practice/ domain (CL-1 s64).
 */
import type { Question } from '../../../content';
import { DjayIdentity } from '../../../mascot';
import { VisualAidFactory, shouldShowVisualAid } from '../../../visuals';
import type { QuestionResolvedSupport } from '../../../support';
import { ExplanationContent } from './ExplanationContent';
import { LevelUpCardReveal, getLevelAchievement } from './LevelUpCardReveal';
import type { AccordionSection } from './FeedbackAccordion';
import { type MoneyToken } from './feedbackAnswerFormatters';
import { formatMoneyDisplay } from '../../../utils/moneyFormatters';
import { resolveCityName, distanceKm } from '../../../visuals/components/topoMap/nameResolver';
import { StepSolverConfig } from '../../../content/types';
// ── Types ────────────────────────────────────────────────────
// S-1 (Support RC audit): renamed from non-existent `SupportContent` to the
// actually-exported `QuestionResolvedSupport` from src/support/index.ts. The
// shape is identical to what was being passed in — this is purely a type
// import fix.
interface SectionBuilderInput {
  question: Question;
  wasCorrect: boolean;
  resolvedSupport: QuestionResolvedSupport;
  distractorExplanation?: string;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  userMoneyTokens: MoneyToken[] | null;
  correctCents: number | null;
  userAnswer?: string;
}
// ── Helpers ──────────────────────────────────────────────────
function getVisualHeaderText(resolvedSupport: QuestionResolvedSupport, isMoneyAssembler: boolean): string {
  if (isMoneyAssembler) return 'Vergelijk de munten';
  if (!resolvedSupport.visual) return 'Kijk eens!';
  switch (resolvedSupport.visual.type) {
    case 'fraction':
      return 'Kijk, zo zie je het!';
    case 'geo':
      return 'Kijk, zo meet je het!';
    case 'rule':
      return 'Onthoud deze regel!';
    case 'column':
      return 'Stap voor stap!';
    default:
      return 'Kijk eens!';
  }
}
function getMoneyStyles(cents: number): string {
  if (cents >= 500) return 'w-14 h-9 rounded-lg bg-slate-500 text-white font-bold text-xs shadow-sm';
  if (cents >= 100) return 'w-9 h-9 rounded-full bg-amber-400 text-amber-900 font-bold text-xs shadow-sm';
  if (cents >= 50) return 'w-9 h-9 rounded-full bg-yellow-400 text-yellow-900 font-bold text-[10px] shadow-sm';
  return 'w-7 h-7 rounded-full bg-orange-500 text-white font-bold text-[9px] shadow-sm';
}
function getDenominationDisplay(cents: number): string {
  return cents >= 100 ? `€${cents / 100}` : `${cents}c`;
}
function MoneyTokenGrid({
  tokens,
  wasCorrect,
  correctCents
}: {
  tokens: MoneyToken[];
  wasCorrect: boolean;
  correctCents: number;
}) {
  return <div className="space-y-4" data-id="element-10805" data-mp="2oendbbnmaxe0">
      <div data-id="element-10806" data-mp="2ofgdtuvzgqnp">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-10807" data-mp="2ofos9r7r8410">
          {wasCorrect ? '✅ Jouw antwoord' : '❌ Jouw antwoord'}
        </h4>
        <div className="bg-white rounded-xl p-3 border border-slate-200" data-id="element-10808" data-mp="17xevc5flntgx">
          <div className="flex flex-wrap gap-2" data-id="element-10809" data-mp="1cx1a4jb2thru">
            {tokens.map((token, idx) => <div key={idx} className="flex items-center gap-1" data-id="element-10810" data-mp="1cv82p1sn2tis" data-mpi={idx}>
                <div className={`flex items-center justify-center ${getMoneyStyles(token.valueCents)}`} data-id="element-10811" data-mp="1hvlzfnosih1i">
                  <span data-id="element-10812" data-mp="p7gep0xqks1o">{getDenominationDisplay(token.valueCents)}</span>
                </div>
                {token.count > 1 && <span className="text-xs font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-md" data-id="element-10813" data-mp="u5i8afssg9re">
                    ×{token.count}
                  </span>}
              </div>)}
          </div>
        </div>
      </div>
      {!wasCorrect && <div data-id="element-10814" data-mp="6gffcbspeuhl">
          <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-10815" data-mp="6fxbou3ppdzn">
            ✅ Correct antwoord
          </h4>
          <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200" data-id="element-10816" data-mp="286ee5uesm5vr">
            <p className="text-lg font-black text-emerald-700" data-id="element-10817" data-mp="24647efj6fbfu">
              {formatMoneyDisplay(correctCents)}
            </p>
          </div>
        </div>}
    </div>;
}
// ── Map Feedback Helpers ─────────────────────────────────────
interface MapTargetPoint {
  cityId?: string;
  name?: string;
  lat: number;
  lon: number;
  label?: string;
}
interface MapPlacement {
  lat: number;
  lon: number;
}
function MapPlaceCitiesBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const targets = data.targetPoints as MapTargetPoint[] | undefined ?? [];
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 35;
  let placements: (MapPlacement | null)[] = [];
  try {
    const parsed = JSON.parse(userAnswer);
    if (Array.isArray(parsed)) placements = parsed;
  } catch {
    /* empty */
  }
  const rows = targets.map((t, i) => {
    const name = t.name ?? resolveCityName(t.cityId) ?? `Stad ${i + 1}`;
    const p = placements[i];
    if (!p || typeof p.lat !== 'number' || typeof p.lon !== 'number') {
      return {
        name,
        ok: false,
        missing: true,
        distance: 0
      };
    }
    const dist = distanceKm(p, {
      lat: t.lat,
      lon: t.lon
    });
    return {
      name,
      ok: dist <= tolKm,
      missing: false,
      distance: dist
    };
  });
  const okCount = rows.filter(r => r.ok).length;
  const total = rows.length;
  return <div className="space-y-3" data-id="element-10818" data-mp="2lfg9e7uw6m2p">
      <div className="text-sm font-bold text-slate-700" data-id="element-10819" data-mp="2pfgw0fs5a3ku">
        {okCount} van de {total} goed geplaatst
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-10820" data-mp="jlz1d32unj7f">
        {rows.map((r, i) => <div key={i} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 border ${r.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-10821" data-mp="nnu0c0xb5ki1" data-mpi={i}>
            <div className="flex items-center gap-2 min-w-0" data-id="element-10822" data-mp="1rlo7cnn92gf9">
              <span className="text-base" data-id="element-10823" data-mp="1nkci4j8afx7l">{r.ok ? '✅' : '❌'}</span>
              <span className={`font-bold text-sm truncate ${r.ok ? 'text-emerald-900' : 'text-rose-900'}`} data-id="element-10824" data-mp="1nkm8w915q8ec">
                {r.name}
              </span>
            </div>
            {!r.ok && <span className="text-[11px] font-bold text-rose-600 whitespace-nowrap" data-id="element-10825" data-mp="2b9x1h38cswhe">
                {r.missing ? 'niet geplaatst' : `${Math.round(r.distance)} km mis`}
              </span>}
          </div>)}
      </div>
    </div>;
}
function MapLocatePointBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const data = (question.data ?? {}) as Record<string, unknown>;
  const target = data.targetPoint as MapTargetPoint | undefined;
  if (!target) return null;
  let click: MapPlacement | null = null;
  try {
    const parsed = JSON.parse(userAnswer);
    if (parsed && typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
      click = {
        lat: parsed.lat,
        lon: parsed.lon
      };
    }
  } catch {
    /* empty */
  }
  if (!click) {
    return <div className="text-sm text-slate-700" data-id="element-10826" data-mp="3kit86rm443q8">
        Je hebt geen pin op de kaart geplaatst.
      </div>;
  }
  const dist = distanceKm(click, {
    lat: target.lat,
    lon: target.lon
  });
  const targetName = target.label ?? target.name ?? (target.cityId ? resolveCityName(target.cityId) : 'de juiste plek');
  return <div className="space-y-2" data-id="element-10827" data-mp="31a3dft749fm">
      <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 bg-rose-50 border border-rose-200" data-id="element-10828" data-mp="31ijdgkm7uv1">
        <span className="text-base" data-id="element-10829" data-mp="71u0wkholf2a">📍</span>
        <span className="font-bold text-sm text-rose-900" data-id="element-10830" data-mp="c289c3n72r0u">
          Je pin lag <strong data-id="element-10831" data-mp="xdyawa29tkpf">{Math.round(dist)} km</strong> van {targetName}.
        </span>
      </div>
    </div>;
}
function StepSolverBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const config = (question.data?.stepSolver || question.data || {}) as StepSolverConfig;
  const steps = config.steps || [];
  let parsedAnswers: Record<number, string> = {};
  try {
    parsedAnswers = JSON.parse(userAnswer);
  } catch {
    /* empty */
  }
  return <div className="space-y-3" data-id="element-10832" data-mp="el45o7ug96ye">
      {steps.map((step, idx) => {
      const userVal = parsedAnswers[idx]?.trim() || '';
      const accepted = step.acceptedAnswers || [step.correctAnswer];
      const isCorrect = accepted.some(ans => {
        const acceptedStr = String(ans).trim();
        if (acceptedStr.toLowerCase() === userVal.toLowerCase()) return true;
        const userNum = parseFloat(userVal.replace(/[€\s,]/g, '.'));
        const acceptedNum = parseFloat(acceptedStr.replace(/[€\s,]/g, '.'));
        if (!isNaN(userNum) && !isNaN(acceptedNum)) {
          return Math.abs(userNum - acceptedNum) < 0.001; // NUMERIC_TOLERANCE
        }
        return false;
      });
      return <div key={idx} className={`p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-10833" data-mp="1snbly54en92u" data-mpi={idx}>
            <div className="flex items-start gap-3" data-id="element-10834" data-mp="1xjsgbh276jod">
              <div className="mt-0.5 text-lg" data-id="element-10835" data-mp="2ld0qgsgbpr97">{isCorrect ? '✅' : '❌'}</div>
              <div className="flex-1 min-w-0" data-id="element-10836" data-mp="2qb2abflkpulq">
                <p className="font-bold text-sm text-slate-800 mb-1" data-id="element-10837" data-mp="2qa0vr15cmpz9">
                  {step.prompt}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-id="element-10838" data-mp="2gcmitbimwjbp">
                  <span className="text-slate-600" data-id="element-10839" data-mp="2geh4is1qcfgu">
                    Jouw antwoord:{' '}
                    <strong className={isCorrect ? 'text-emerald-700' : 'text-rose-700'} data-id="element-10840" data-mp="10xyutamrs7oq">
                      {userVal || '-'}
                    </strong>
                  </span>
                  {!isCorrect && <span className="text-slate-600" data-id="element-10841" data-mp="3pfoqcm7sdmtv">
                      Correct:{' '}
                      <strong className="text-emerald-700" data-id="element-10842" data-mp="3bkcxkkd6y6fl">
                        {step.correctAnswer}
                      </strong>
                    </span>}
                </div>
                {!isCorrect && (step.explanation || step.hint) && <div className="mt-2 text-sm text-slate-600 italic bg-white/50 p-2 rounded-lg" data-id="element-10843" data-mp="36oxmg06kiomv">
                    💡 {step.explanation || step.hint}
                  </div>}
              </div>
            </div>
          </div>;
    })}
    </div>;
}
function TrueFalseSeriesBreakdown({
  question,
  userAnswer
}: {
  question: Question;
  userAnswer: string;
}) {
  const config = (question.data || {}) as {
    statements?: {
      text: string;
      isTrue: boolean;
      explanation?: string;
    }[];
  };
  const statements = config.statements || [];
  let userPicks: boolean[] = [];
  try {
    const parsed = JSON.parse(userAnswer);
    if (Array.isArray(parsed)) userPicks = parsed;
  } catch {
    /* empty */
  }
  return <div className="space-y-3" data-id="element-10844" data-mp="2kf7auldlrvzv">
      {statements.map((stmt, idx) => {
      const pick = userPicks[idx];
      const answered = typeof pick === 'boolean';
      const isCorrect = answered && pick === stmt.isTrue;
      return <div key={idx} className={`p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-10845" data-mp="2u3z0unz3s685" data-mpi={idx}>
            <div className="flex items-start gap-3" data-id="element-10846" data-mp="3356yr4xpj51p">
              <div className="mt-0.5 text-lg" data-id="element-10847" data-mp="333ce7rqcley5">{isCorrect ? '✅' : '❌'}</div>
              <div className="flex-1 min-w-0" data-id="element-10848" data-mp="1dr0oggygmvji">
                <p className="font-bold text-sm text-slate-800 mb-1" data-id="element-10849" data-mp="18rntta1ckk6d">
                  {stmt.text}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-id="element-10850" data-mp="38ugnaitkzj5g">
                  <span className="text-slate-600" data-id="element-10851" data-mp="3ds8bp2v0on4w">
                    Jij koos:{' '}
                    <strong className={isCorrect ? 'text-emerald-700' : 'text-rose-700'} data-id="element-10852" data-mp="3is34jbycrmsl">
                      {answered ? pick ? 'Waar' : 'Onwaar' : '-'}
                    </strong>
                  </span>
                  {!isCorrect && <span className="text-slate-600" data-id="element-10853" data-mp="kx6aybxt1j8">
                      Juist:{' '}
                      <strong className="text-emerald-700" data-id="element-10854" data-mp="5dzq4eqwwwm8">
                        {stmt.isTrue ? 'Waar' : 'Onwaar'}
                      </strong>
                    </span>}
                </div>
                {!isCorrect && stmt.explanation && <div className="mt-2 text-sm text-slate-600 italic bg-white/50 p-2 rounded-lg" data-id="element-10855" data-mp="jcg8j2y50bk7">
                    💡 {stmt.explanation}
                  </div>}
              </div>
            </div>
          </div>;
    })}
    </div>;
}
// ── Main Builder ─────────────────────────────────────────────
export function buildAccordionSections(input: SectionBuilderInput): AccordionSection[] {
  const {
    question,
    wasCorrect,
    resolvedSupport,
    distractorExplanation,
    levelUp,
    userMoneyTokens,
    correctCents,
    userAnswer
  } = input;
  const isMoneyAssembler = question.questionType === 'money';
  const supportVisuals = question.supportAids || [];
  const showSupportVisuals = supportVisuals.some(aid => shouldShowVisualAid(aid, wasCorrect));
  const sections: AccordionSection[] = [];
  // 0. Level Up Info — rich collectible-card reveal when the reached level
  // has an achievement card (levels 3/5/7/10/12/15); otherwise a celebratory
  // line. See APP_FEEL_2027_UPGRADE §P3 "Voortgang is Voelbaar".
  if (levelUp && wasCorrect) {
    const levelAchievement = getLevelAchievement(levelUp.level);
    sections.push({
      id: 'levelup',
      icon: <span className="text-lg" data-id="element-10856" data-mp="2q2m74rz5vzj6">🌟</span>,
      title: levelAchievement ? `Kaart vrijgespeeld — ${levelAchievement.name}` : `Level ${levelUp.level} — ${levelUp.title}`,
      subtitle: levelAchievement ? `Level ${levelUp.level} bereikt · ${levelUp.xp} XP` : `Je hebt nu ${levelUp.xp} XP!`,
      content: <LevelUpCardReveal level={levelUp.level} xp={levelUp.xp} title={levelUp.title} data-id="element-10857" data-mp="34rik93g2s2qn" />
    });
  }
  // 1. Explanation (only for correct answers — wrong answers show explanation directly)
  if (wasCorrect && resolvedSupport.explanation) {
    sections.push({
      id: 'explanation',
      icon: <DjayIdentity size="sm" emotion="happy" animate={false} data-id="element-10858" data-mp="3npuy32hxm0sk" />,
      title: 'Waarom dit klopt',
      content: <ExplanationContent explanation={resolvedSupport.explanation || ''} distractorExplanation={undefined} isCorrect={wasCorrect} solutionSteps={resolvedSupport.solutionSteps} data-id="element-10859" data-mp="3sh38o1uzzs7b" />
    });
  }
  // 1.5 Map question per-city / per-pin breakdown
  if (question.questionType === 'map_place_cities') {
    sections.push({
      id: 'map_breakdown',
      icon: <span className="text-lg" data-id="element-10860" data-mp="agfpkun0mys9">🗺️</span>,
      title: wasCorrect ? 'Jouw plaatsingen' : 'Per stad bekeken',
      content: <MapPlaceCitiesBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-10861" data-mp="3ix138oqleg1p" />
    });
  }
  if (question.questionType === 'map_locate_point' && !wasCorrect) {
    sections.push({
      id: 'map_distance',
      icon: <span className="text-lg" data-id="element-10862" data-mp="3nvvk3l501uj6">📍</span>,
      title: 'Hoe ver zat je ernaast?',
      content: <MapLocatePointBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-10863" data-mp="1847q9yla8i4l" />
    });
  }
  // 1.6 Step Solver Breakdown
  if (question.questionType === 'step_solver') {
    sections.push({
      id: 'step_solver_breakdown',
      icon: <span className="text-lg" data-id="element-10864" data-mp="1b85h9p4vjckk">📝</span>,
      title: 'Stap voor stap bekeken',
      content: <StepSolverBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-10865" data-mp="1f9qvbdt59wwc" />
    });
  }
  // 1.7 True/False Series per-statement breakdown
  if (question.questionType === 'true_false_series') {
    sections.push({
      id: 'true_false_breakdown',
      icon: <span className="text-lg" data-id="element-10866" data-mp="rg0fkf6guyqx">⚖️</span>,
      title: wasCorrect ? 'Alle stellingen' : 'Stelling voor stelling bekeken',
      content: <TrueFalseSeriesBreakdown question={question} userAnswer={userAnswer ?? ''} data-id="element-10867" data-mp="11fr8wdjaj7pf" />
    });
  }
  // 2. Visual Aids
  if (!isMoneyAssembler && showSupportVisuals) {
    sections.push({
      id: 'visual',
      icon: <DjayIdentity size="sm" emotion="pointing" animate={false} data-id="element-10868" data-mp="8rbxe0zdb7iu" />,
      title: getVisualHeaderText(resolvedSupport, isMoneyAssembler),
      content: <div className="space-y-4" data-id="element-10869" data-mp="67fldmfczyam">
          {supportVisuals.filter(aid => shouldShowVisualAid(aid, wasCorrect)).map((aid, index) => <VisualAidFactory key={index} visualAid={aid} isCorrect={wasCorrect} mode="support" data-id="element-10870" data-mp="3sedu4iaqazrz" data-mpi={index} />)}
        </div>
    });
  }
  // 3. Money Assembler Feedback
  if (isMoneyAssembler && userMoneyTokens && correctCents !== null) {
    sections.push({
      id: 'money',
      icon: <span className="text-lg" data-id="element-10871" data-mp="3elo7cbndlqtt">🪙</span>,
      title: wasCorrect ? 'Jouw munten' : 'Vergelijk de munten',
      content: <MoneyTokenGrid tokens={userMoneyTokens} wasCorrect={wasCorrect} correctCents={correctCents} data-id="element-10872" data-mp="39ljmyajckbyb" />
    });
  }
  return sections;
}